/*
 * Wrapper imports.
 * Events, methods, and properties that we want to wrap/add to the native Peer.
 */
import events from './events'
import methods from './methods'
import properties from './properties'

import { mergeValues } from '../utils'
import defaults from './config'
import { logManager } from '../logs'

// Libraries.
import EventEmitter from 'eventemitter3'
import { createTimer } from './utils/timer'

/**
 * Create a Proxied Peer.
 * This Peer is a native PeerConnection that has had some new functionality
 *    added and some existing functionality "augmented". Where an event, a
 *    method, or a property has been defined (see folders), we intercept/add
 *    that functionality.
 * @method peer
 * @return {Peer}
 */
export default function peer (id, config = {}, trackManager) {
  const log = logManager.getLogger('Peer', id)
  config = mergeValues(defaults, config)
  log.info('Creating new Peer.')

  const iceTimer = createTimer()
  const emitter = new EventEmitter()

  // Create the native Peer.
  log.debug('Creating native PeerConnection.', config.rtcConfig)
  const nativePeer = new RTCPeerConnection(config.rtcConfig, { optional: [{ googDscp: true }] })

  // Add the event emitter methods to the wrapped methods as well.
  const customMethods = {
    ...methods,
    on: emitter.on.bind(emitter),
    off: emitter.off.bind(emitter),
    once: emitter.once.bind(emitter)
  }

  /**
   * The Peer model.
   * @typedef {Peer}
   * @property {RTCPeerConnection} peer     The native PeerConnection.
   * @property {string}            id       The unique identifier for the Peer.
   * @property {boolean}           dtlsRole The DTLS role selected for this PeerConnection. Set after the initial negotiation is completed.
   * @property {Object}            trackManager Manager that tracks all MediaStreamTracks.
   * @property {Object}            log      Logger specific to this Peer.
   * @property {Object}            iceTimer Timer tool (specifically for ICE collection).
   * @property {EventEmitter}      emitter
   * @property {Array<RTCIceCandidate>} iceCandidates Gathered candidates.
   * @property {timeoutID} [iceLoop] Reference to the on-going ICE collection loop.
   */
  const base = {
    nativePeer,
    proxyPeer: undefined,
    id,
    dtlsRole: null,
    config,
    trackManager,
    log,
    iceTimer,
    emitter,
    iceCandidates: [],
    iceLoop: undefined
  }

  /**
   * Create the Proxy object that will be used as the PeerConnection.
   *
   * This will intercept all `get` and `set` operations and either forward them
   *    directly to the real PeerConnection or to our wrapped operations.
   * This allows us to shim part or all of the PeerConnection's API. We can
   *    decide which operations we want to intercept while allowing others to
   *    pass through unchanged.
   */
  base.proxyPeer = new Proxy(base, {
    /**
     * Intercept "get" calls on the Proxy.
     * This function is called anytime a property on `base` is accessed,
     *    eg. `base.someProp`
     * @param {Object} target    base
     * @param {string} prop      Property being accessed.
     * @param {Object} receiver  The Proxy object.
     */
    get: function (target, prop, receiver) {
      if (typeof base.nativePeer[prop] === 'function') {
        /*
         * If a function is being accessed, determine whether we want to
         *    return the native function or our own.
         */
        if (Object.keys(customMethods).includes(prop)) {
          // Return our wrapped version of the original function.
          return customMethods[prop].bind(base)
        } else {
          // Return the original function, bound to have the original context.
          return base.nativePeer[prop].bind(base.nativePeer)
        }
      } else if (customMethods[prop] && typeof customMethods[prop] === 'function') {
        /*
         * If a non-native function is being accessed, return our custom method.
         * These are methods that are not on the native RTCPeerConnection
         *    object, but we added to the old Peer model.
         */
        // TODO: Move all of this functionality to a higher level of abstraction.
        return customMethods[prop].bind(base)
      } else if (properties[prop]) {
        /*
         * If a PeerConnection property is being accessed, and we have a
         *    wrapping for it, return our wrapper property.
         */
        if (typeof properties[prop] === 'function') {
          // If our property wrapper is a function, call the function and return
          //    the results.
          return properties[prop].bind(base)()
        } else {
          // Otherwise, just return the original results.
          return base.nativePeer[prop]
        }
      } else if (prop !== 'nativePeer' && base[prop]) {
        /*
         * If a Peer model property, other than the peer, is being accessed,
         *    return it. The Peer model shouldn't have any properties that the
         *    PeerConnection also has (to avoid conflicts).
         */
        return base[prop]
      } else {
        // Otherwise, return the property from the PeerConnection itself.
        return base.nativePeer[prop]
      }
    },

    /**
     * Intercept "set" calls on the Proxy.
     * This function is called anytime a property on `base` is assigned,
     *    eg. `base.someProp = value`
     * @method
     * @param  {Object} target   base
     * @param  {string} prop     Property being set.
     * @param  {Any}    value    The value being set.
     * @param  {Proxy}  receiver The Proxy object.
     * @return {boolean}         Whether the assignment succeeded or not.
     */
    set: function (target, prop, value, receiver) {
      if (events[prop]) {
        // If a value is being set on one of our "wrapped events", then call
        //    the "event function".
        return events[prop].bind(base)(value)
      } else if (prop === 'dtlsRole') {
        /**
         * Only allow the `dtlsRole` property of the Peer model be set. The
         *    Session needs to be able to get/set this property, in the case
         *    where it recreates the Peer multiple times, so it needs to set the
         *    role to something specific.
         * Reference: KAA-1816
         */
        base.dtlsRole = value
        return true
      } else {
        // Otherwise, try to set the value on the native Peer.
        return Reflect.set(base.nativePeer, prop, value)
      }
    }
  })

  /**
   * For event debugging purposes, start with a dummy listener for every event.
   *    This ensures our Proxy is listening for the events (in the case nothing
   *    else is listening), so that debug information from the event wrappers
   *    are logged.
   */
  for (const eventName in events) {
    base.proxyPeer[eventName] = () => {}
  }

  return base.proxyPeer
}
